import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Loader2 } from 'lucide-react';
import axios from 'axios';
import SocialShare from '../components/SocialShare';
import CommentSection from '../components/CommentSection';
import RelatedContent from '../components/RelatedContent';
import { ClickToTweet } from '../components/ClickToTweet';
import SEO, { buildArticleJsonLd, buildBreadcrumbJsonLd } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/posts/${slug}`);
        setPost(response.data);
        setError(null);
        window.scrollTo(0, 0);
      } catch (err) {
        console.error('Error fetching post:', err);
        if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('Failed to load post. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error === 'not_found' || !post) {
    return (
      <div className="min-h-screen pt-32 px-6" data-testid="post-not-found">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-light mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-black transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft size={20} />
            <span>Return to Home</span>
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-error">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="post-detail-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/post/${post.slug}`}
        image={post.og_image || post.image}
        type="article"
        publishedAt={post.created_at || post.date}
        updatedAt={post.updated_at || post.created_at || post.date}
        author={post.author}
        keywords={[...(post.tags || []), post.category, 'AI ethics', 'responsible AI']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            buildArticleJsonLd({
              title: post.title,
              description: post.excerpt,
              slug: post.slug,
              image: post.og_image || post.image,
              author: post.author,
              publishedAt: post.created_at || post.date,
              updatedAt: post.updated_at || post.created_at || post.date,
              keywords: post.tags || [],
              category: post.category,
            }),
            buildBreadcrumbJsonLd([
              { name: 'Home', url: '/' },
              { name: 'Topics', url: '/topics' },
              { name: post.category, url: `/topics#${post.categorySlug}` },
              { name: post.title, url: `/post/${post.slug}` },
            ]),
          ],
        }}
      />
      {/* Back Button */}
      <div className="pt-32 pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-black transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Category */}
          <Link
            to={`/topics#${post.categorySlug}`}
            className="inline-block text-sm font-medium text-gray-600 hover:text-black transition-colors mb-4"
            data-testid="post-category"
          >
            {post.category}
          </Link>

          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight" data-testid="post-title">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 mb-8 text-gray-600" data-testid="post-meta">
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={18} />
              <span>{post.readTime}</span>
            </div>
          </div>

          {/* Featured Image */}
          {post.image && (
            <div className="mb-12 rounded-lg overflow-hidden aspect-[16/9] bg-gray-100">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
                data-testid="post-image"
              />
            </div>
          )}

          {/* TL;DR — quotable summary AI search engines love */}
          {post.tldr ? (
            <aside
              className="mb-10 p-6 rounded-xl border-l-4 border-blue-500 bg-blue-50/50"
              data-testid="post-tldr"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">TL;DR</h2>
              <p className="text-gray-800 leading-relaxed text-base">{post.tldr}</p>
            </aside>
          ) : null}

          {/* Content */}
          <ClickToTweet slug={post.slug}>
          <div className="prose prose-lg max-w-none mb-12" data-testid="post-content">
            {post.content.split('\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-3xl font-light mt-12 mb-6">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="text-2xl font-light mt-8 mb-4">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              }
              if (paragraph.startsWith('> ')) {
                return (
                  <blockquote key={index} className="my-6 pl-5 border-l-4 border-gray-300 italic text-xl text-gray-700 leading-relaxed">
                    {paragraph.replace('> ', '')}
                  </blockquote>
                );
              }
              if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return (
                  <p key={index} className="mb-4 font-semibold text-gray-800">
                    {paragraph.replace(/\*\*/g, '')}
                  </p>
                );
              }
              if (paragraph.startsWith('- ')) {
                return (
                  <li key={index} className="ml-6 mb-2 text-gray-700">
                    {paragraph.replace('- ', '')}
                  </li>
                );
              }
              if (paragraph.trim() === '') {
                return null;
              }
              return (
                <p key={index} className="mb-6 text-gray-700 leading-relaxed">
                  {paragraph}
                </p>
              );
            })}
          </div>
          </ClickToTweet>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mb-12 pb-12 border-b border-gray-200" data-testid="post-tags">
              <Tag size={18} className="text-gray-600" />
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-gray-100 text-sm text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Social Share */}
          <div className="mb-12 pb-12 border-b border-gray-200">
            <SocialShare title={post.title} slug={post.slug} />
          </div>

          {/* Related Lessons (cross-link for SEO) */}
          <RelatedContent kind="post" slug={post.slug} />

          {/* Comments Section */}
          <CommentSection postSlug={post.slug} />
        </div>
      </article>
    </div>
  );
};

export default PostDetail;
